import { useEffect, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import {
  useArchiveContactSubmission,
  useContactSubmission,
  useContactSubmissions,
  useUpdateContactSubmission,
} from "@/features/contact/hooks";
import type {
  ContactSubmission,
  ContactSubmissionFilters,
  ContactSubmissionStatus,
  ContactSubmissionType,
} from "@/features/contact/api";

const statuses: Array<ContactSubmissionStatus | "ALL"> = ["ALL", "NEW", "REVIEWING", "ACCEPTED", "DECLINED", "COMPLETED", "ARCHIVED"];
const types: Array<ContactSubmissionType | "ALL"> = ["ALL", "GENERAL", "COMMISSION"];

export function ContactSubmissionsPage() {
  const [filters, setFilters] = useState<ContactSubmissionFilters>({ page: 1, pageSize: 10, type: "ALL", status: "ALL" });
  const submissions = useContactSubmissions(filters);
  const updateSubmission = useUpdateContactSubmission();
  const archiveSubmission = useArchiveContactSubmission();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useContactSubmission(selectedId);
  const [archiveTarget, setArchiveTarget] = useState<ContactSubmission | null>(null);
  const [status, setStatus] = useState<ContactSubmissionStatus>("NEW");
  const [adminNotes, setAdminNotes] = useState("");
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (selected.data) {
      setStatus(selected.data.status);
      setAdminNotes(selected.data.adminNotes ?? "");
      setMessage(null);
    }
  }, [selected.data]);

  function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;
    updateSubmission.mutate(
      { id: selectedId, input: { status, adminNotes: adminNotes || null } },
      {
        onSuccess: () => setMessage({ tone: "success", text: "Submission updated." }),
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to update submission." }),
      },
    );
  }

  const totalPages = submissions.data ? Math.max(1, Math.ceil(submissions.data.total / submissions.data.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Contact Submissions" description="Review general messages and commission requests." />
      <div className="grid gap-3 rounded-md border border-cyan-300/15 bg-black/25 p-4 lg:grid-cols-[1fr_12rem_12rem]">
        <FilterText value={filters.search ?? ""} onChange={(search) => setFilters((current) => ({ ...current, search, page: 1 }))} />
        <FilterSelect label="Type" value={filters.type ?? "ALL"} options={types} onChange={(type) => setFilters((current) => ({ ...current, type: type as ContactSubmissionFilters["type"], page: 1 }))} />
        <FilterSelect label="Status" value={filters.status ?? "ALL"} options={statuses} onChange={(nextStatus) => setFilters((current) => ({ ...current, status: nextStatus as ContactSubmissionFilters["status"], page: 1 }))} />
      </div>

      <AdminDataTable
        rows={submissions.data?.items ?? []}
        getRowKey={(submission) => submission.id}
        emptyState={<EmptyState title={submissions.isLoading ? "Loading submissions..." : "No submissions found"} description="New public contact submissions will appear here." />}
        columns={[
          { key: "created", header: "Created", render: (row) => new Date(row.createdAt).toLocaleDateString() },
          { key: "type", header: "Type", render: (row) => <StatusBadge tone={row.type === "COMMISSION" ? "warning" : "muted"}>{row.type}</StatusBadge> },
          { key: "name", header: "Name", render: (row) => <div><p className="font-medium text-white">{row.name}</p><p className="text-xs text-content-muted">{row.email}</p>{row.discord ? <p className="text-xs text-content-muted">{row.discord}</p> : null}</div> },
          { key: "project", header: "Subject / Project", render: (row) => row.projectName || row.subject || <span className="text-content-muted">None</span> },
          { key: "commissionType", header: "Commission", render: (row) => row.commissionType ? <StatusBadge tone="muted">{row.commissionType}</StatusBadge> : <span className="text-content-muted">-</span> },
          { key: "status", header: "Status", render: (row) => <SubmissionStatusBadge status={row.status} /> },
          { key: "actions", header: "Actions", className: "px-4 py-4 text-right", render: (row) => <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setSelectedId(row.id)}>View</Button><Button size="sm" variant="danger" onClick={() => setArchiveTarget(row)}>Archive</Button></div> },
        ]}
      />

      {submissions.data ? (
        <div className="flex items-center justify-between text-sm text-content-secondary">
          <span>Page {submissions.data.page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={(filters.page ?? 1) <= 1} onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) - 1 }))}>Prev</Button>
            <Button size="sm" variant="ghost" disabled={(filters.page ?? 1) >= totalPages} onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}>Next</Button>
          </div>
        </div>
      ) : null}

      <AdminFormDrawer isOpen={selectedId !== null} title="Submission Details" onClose={() => setSelectedId(null)}>
        {selected.isLoading ? <p className="text-content-secondary">Loading...</p> : selected.data ? (
          <form className="space-y-5" onSubmit={handleSave}>
            <Detail submission={selected.data} />
            <label className="block">
              <span className="text-sm text-content-secondary">Status</span>
              <select className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={status} onChange={(event) => setStatus(event.target.value as ContactSubmissionStatus)}>
                {statuses.filter((item) => item !== "ALL").map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-content-secondary">Admin Notes</span>
              <textarea className="mt-2 min-h-32 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white" value={adminNotes} onChange={(event) => setAdminNotes(event.target.value)} />
            </label>
            {message ? <p className={message.tone === "success" ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100" : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"}>{message.text}</p> : null}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="ghost" onClick={() => setSelectedId(null)}>Close</Button>
              <Button type="submit" disabled={updateSubmission.isPending}>{updateSubmission.isPending ? "Saving..." : "Save"}</Button>
            </div>
          </form>
        ) : <EmptyState title="Submission unavailable" description="This submission could not be loaded." />}
      </AdminFormDrawer>

      <ConfirmDeleteDialog
        isOpen={archiveTarget !== null}
        title="Archive Submission"
        description={`Archive submission from "${archiveTarget?.name ?? "this sender"}"?`}
        isPending={archiveSubmission.isPending}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={() => archiveTarget && archiveSubmission.mutate(archiveTarget.id, { onSuccess: () => setArchiveTarget(null) })}
      />
    </div>
  );
}

function Detail({ submission }: { submission: ContactSubmission }) {
  return (
    <div className="space-y-4 rounded-md border border-cyan-300/15 bg-black/20 p-4 text-sm">
      <Row label="Name" value={submission.name} />
      <Row label="Email" value={submission.email} />
      <Row label="Discord" value={submission.discord} />
      <Row label="Subject" value={submission.subject} />
      <Row label="Commission Type" value={submission.commissionType} />
      <Row label="Project" value={submission.projectName} />
      <Row label="Community" value={submission.communityName} />
      <Row label="Budget" value={submission.budget} />
      <Row label="Deadline" value={submission.deadline ? new Date(submission.deadline).toLocaleDateString() : null} />
      <Row label="Reference Links" value={submission.referenceLinks} />
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-content-muted">Message</p>
        <p className="mt-1 whitespace-pre-line text-content-secondary">{submission.message}</p>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return <div><p className="text-xs uppercase tracking-[0.18em] text-content-muted">{label}</p><p className="mt-1 text-content-secondary">{value}</p></div>;
}

function SubmissionStatusBadge({ status }: { status: ContactSubmissionStatus }) {
  const tone = status === "NEW" ? "warning" : status === "ACCEPTED" || status === "COMPLETED" ? "active" : status === "DECLINED" || status === "ARCHIVED" ? "inactive" : "muted";
  return <StatusBadge tone={tone}>{status}</StatusBadge>;
}

function FilterText({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label><span className="text-xs uppercase tracking-[0.18em] text-content-muted">Search</span><input className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return <label><span className="text-xs uppercase tracking-[0.18em] text-content-muted">{label}</span><select className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>;
}
